<template>
  <div class="container">
    <div class="page-item">
      <!-- Search Bar with Role Dropdown Button -->
      <div class="search-bar">
        <input
            type="text"
            placeholder="Search by name, business name, or mobile..."
            class="search-input"
            v-model="searchFilter"
        />
        <button class="role-btn" @click="toggleRoleDropdown">
          <i class="fa fa-filter"></i>
          <span v-if="selectedRole"> {{ selectedRole }} </span>
        </button>

        <!-- Role Dropdown -->
        <div v-if="showRoleDropdown" class="role-dropdown">
          <div
              class="role-item"
              :class="{ active: selectedRole === '' }"
              @click="selectRole('')"
          >
            All Roles
          </div>
          <div
              v-for="role in userRoles"
              :key="role.name"
              class="role-item"
              :class="{ active: selectedRole === role.name }"
              @click="selectRole(role.name)"
          >
            {{ role.name }}
          </div>
        </div>
      </div>

      <button
          type="button"
          class="btn button-default create-user-btn"
          @click="newUser"
      >
        Create User
      </button>
    </div>

    <!-- Loader -->
    <div v-if="loading" class="loading-spinner">
      <i class="fa fa-spinner fa-spin"></i> Loading...
    </div>

    <!-- User Table -->
    <div v-else>
      <div class="table-container">
        <table>
          <thead>
          <tr>
            <th scope="col">Name</th>
            <th scope="col">Mobile Number</th>
            <th scope="col">User Role</th>
            <th scope="col">Reporting To</th>
            <th scope="col">Status</th>
            <th scope="col">Actions</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="user in filteredUsers" :key="user.id">
            <td>
              {{ user.full_name }}
              <div v-if="user.business_name" class="business-name">({{ user.business_name }})</div>
            </td>
            <td>{{ user.mobile }}</td>
            <td>{{ user.role_name }}</td>
            <td>{{ user.reporting_to_name || '-' }}</td>
            <td>
                <span
                    :class="{
                    'status-label status-yes': user.is_active === '1',
                    'status-label status-no': user.is_active === '0'
                  }"
                >
                  {{ user.is_active === '1' ? 'Active' : 'Inactive' }}
                </span>
            </td>
            <td>
              <i class="fa fa-edit" @click="editDetails(user.id)"></i>
              <i
                  class="fa fa-eye"
                  aria-hidden="true"
                  @click="viewDetails(user.id)"
              ></i>
              <i
                  v-if="user.is_blocked === '1'"
                  class="fa fa-unlock unblock-icon"
                  title="Unblock User"
                  @click="confirmUnblock(user.id)"
              ></i>
            </td>
          </tr>
          <tr v-if="filteredUsers.length === 0">
            <td colspan="6" class="text-center">No data available</td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- View User Modal -->
    <ViewUserModal
        :showModal="showPopup"
        :userId="selectedUserId"
        :token="token"
        @close="closePopup"
    />

    <!-- Unblock Confirmation Dialog -->
    <div v-if="showUnblockDialog" class="dialog-overlay">
      <div class="dialog-box">
        <div class="dialog-header">
          <h3>Confirm user unblock</h3>
        </div>
        <div class="dialog-body">
          <p>This user account was blocked. Are you want to unblock this user now?</p>
        </div>
        <div class="dialog-footer">
          <button class="btn btn-danger" @click="cancelUnblock">No</button>
          <button class="btn btn-success" @click="processUnblock">
            <span v-if="unblocking" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            <span v-if="!unblocking">Yes</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { fetchUsersList, fetchUserRoleListForUserForm, unblockUser} from "@/services/dataService";
import ViewUserModal from "@/pages/Modal/ViewUserModal.vue";
import { useToast } from "vue-toastification";

export default {
  name: "UserList",
  components: {
    ViewUserModal,
  },
  data() {
    return {
      userList: [],
      userRoles: [],
      loading: false,
      searchFilter: "",
      selectedRole: "",
      showRoleDropdown: false,
      token: localStorage.getItem("token_set") || "",
      showPopup: false,
      selectedUserId: null,
      showUnblockDialog: false,
      unblockUserId: null,
      unblocking: false,
    };
  },

  computed: {
    filteredUsers() {
      // Deduplicate by id first to handle API returning duplicate records
      const seen = new Set();
      const uniqueList = this.userList.filter((user) => {
        if (seen.has(user.id)) return false;
        seen.add(user.id);
        return true;
      });

      const filterText = this.searchFilter ? this.searchFilter.trim().toLowerCase() : '';

      const filtered = uniqueList.filter((user) => {
        // Text search: only full_name, business_name, and mobile
        const matchesSearch =
            !filterText ||
            (user.full_name || '').toLowerCase().includes(filterText) ||
            (user.mobile || '').toLowerCase().includes(filterText) ||
            (user.business_name || '').toLowerCase().includes(filterText);

        // Role filter via dropdown
        const matchesRole =
            !this.selectedRole || user.role_name === this.selectedRole;

        return matchesSearch && matchesRole;
      });

      return filtered.sort((a, b) =>
          (a.full_name || '').localeCompare(b.full_name || '')
      );
    },
  },

  setup() {
    const toast = useToast();

    return {
      toast,
    };
  },

  methods: {
    async fetchUsers() {
      try {
        this.loading = true;
        const response = await fetchUsersList(this.token);
        this.userList = response.data?.data || [];
      } catch (error) {
        console.error("Error fetching user list:", error);
        this.userList = [];
      } finally {
        this.loading = false;
      }
    },

    async fetchRoles() {
      try {
        const response = await fetchUserRoleListForUserForm(this.token);
        this.userRoles = response.data?.data || [];
      } catch (error) {
        console.error("Error fetching roles:", error);
        this.userRoles = [];
      }
    },

    toggleRoleDropdown() {
      this.showRoleDropdown = !this.showRoleDropdown;
    },

    selectRole(role) {
      this.selectedRole = role;
      this.showRoleDropdown = false;
    },

    newUser() {
      this.$router.push({ path: "/create-user" });
    },

    viewDetails(id) {
      this.selectedUserId = id;
      this.showPopup = true;
      document.body.style.overflow = "hidden";
    },

    editDetails(id) {
      this.$router.push({ path: `/edit-user/${id}` });
    },

    confirmUnblock(id) {
      this.unblockUserId = id;
      this.showUnblockDialog = true;
      document.body.style.overflow = "hidden";
    },

    async processUnblock() {
      try {
        this.unblocking = true;

        const response = await unblockUser(this.unblockUserId, this.token);

        if (response.data.status === "success") {
          this.toast.success(response.data.message);

          // Close dialog and reset variables
          this.showUnblockDialog = false;
          this.unblockUserId = null;

          // Refresh user list after successful unblock
          await this.fetchUsers();

        } else {
          this.toast.error(response.data.message);
        }
      } catch (e) {
        this.toast.error("User unblock failed. Please try again.");
      } finally {
        this.unblocking = false;
      }
    },

    cancelUnblock() {
      this.showUnblockDialog = false;
      this.unblockUserId = null;
      document.body.style.overflow = "auto";
    },

    closePopup() {
      this.showPopup = false;
      this.selectedUserId = null;
      document.body.style.overflow = "auto";
    },
  },

  mounted() {
    this.fetchUsers();
    this.fetchRoles();
  },

  beforeUnmount() {
    document.body.style.overflow = "auto";
  },
};
</script>

<style scoped>
/* Container */
.container {
  background-color: #ffffff;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 20px;
  margin: 10px;
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
}

/* Loading Spinner */
.loading-spinner {
  font-size: 18px;
  text-align: center;
  margin-top: 20px;
  color: #007bff;
}

/* Top search and create user section */
.page-item {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 10px;
  margin-bottom: 15px;
}

/* Combined search bar and filter button */
.search-bar {
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;
}

/* Search input */
.search-input {
  width: 100%;
  padding: 0 14px;
  border-radius: 6px 0 0 6px;
  border: 1px solid #ccc;
  font-size: 14px;
  transition: border-color 0.3s, box-shadow 0.3s;
  height: 42px; /* fixed height */
}
.search-input:focus {
  border-color: #007bff;
  box-shadow: 0 0 5px rgba(38, 143, 255, 0.3);
  outline: none;
}

/* Filter button at the end of search */
.role-btn {
  height: 42px; /* fixed height to match input */
  padding: 0 14px;
  border: 1px solid #ccc;
  border-left: none;
  border-radius: 0 6px 6px 0;
  background: #f8f9fa;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  line-height: 20px;
  box-sizing: border-box;
}
.role-btn:hover {
  background-color: #e9ecef;
}

/* Dropdown menu */
.role-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 6px;
  width: 200px;
  max-height: 250px;
  overflow-y: auto;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  z-index: 10;
  margin-top: 4px;
}

/* Dropdown item */
.role-item {
  padding: 8px 12px;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.2s;
}
.role-item:hover {
  background-color: #f1f1f1;
}
.role-item.active {
  background-color: #007bff;
  color: #fff;
}

/* Create User Button */
.create-user-btn {
  white-space: nowrap;
  padding: 10px 20px;
  border-radius: 6px;
  background-color: #007bff;
  color: #fff;
  border: none;
  cursor: pointer;
  font-size: 14px;
}
.create-user-btn:hover {
  background-color: #0069d9;
}

/* Scrollable Table Container */
.table-container {
  width: 100%;
  overflow-x: auto;
}

/* Table */
table {
  width: 100%;
  min-width: 900px; /* increased from 800px to accommodate new column */
  border-collapse: collapse;
  margin: 10px 0;
  border: none; /* remove table border */
}

/* Table Header */
th {
  text-align: left;
  border-bottom: 2px solid #ddd; /* subtle bottom border for header */
  background-color: #f4f4f4;
  padding: 10px;
  white-space: nowrap;
}

/* Table Data */
td {
  text-align: left;
  border: none; /* remove individual cell borders */
  padding: 10px;
  white-space: nowrap;
  border-bottom: 1px solid #eee; /* row separator */
}

/* Optional: remove last row separator for cleaner look */
tbody tr:last-child td {
  border-bottom: none;
}

/* Status labels */
.status-label {
  display: inline-block;
  min-width: 90px;
  text-align: center;
  padding: 4px 10px;
  border-radius: 5px;
  font-size: 13px;
  font-weight: 500;
  color: #fff;
}
.status-yes {
  background-color: #28a745;
}
.status-no {
  background-color: #dc3545;
}

/* Action icons */
.fa-eye {
  cursor: pointer;
  transition: color 0.3s, transform 0.3s;
  margin-left: 5px;
}
.fa-eye:hover {
  color: #0056b3;
  transform: scale(1.2);
}
.fa-eye:active {
  transform: scale(1.1);
}

.fa-edit {
  cursor: pointer;
  transition: color 0.3s, transform 0.3s;
}
.fa-edit:hover {
  color: #0056b3;
  transform: scale(1.2);
}
.fa-edit:active {
  transform: scale(1.1);
}

.fa-unlock {
  cursor: pointer;
  transition: color 0.3s, transform 0.3s;
  margin-left: 5px;
}
.fa-unlock:hover {
  color: #0056b3;
  transform: scale(1.2);
}
.fa-unlock:active {
  transform: scale(1.1);
}

/* Scrollbar for dropdown */
.role-dropdown::-webkit-scrollbar {
  width: 6px;
}
.role-dropdown::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 6px;
}
.role-dropdown::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 6px;
}
.role-dropdown::-webkit-scrollbar-thumb:hover {
  background: #999;
}

/* Center text when no data */
.text-center {
  text-align: center;
}

/* Dialog Overlay Styles */
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.dialog-box {
  background: white;
  border-radius: 8px;
  width: 400px;
  max-width: 90%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

.dialog-header {
  padding: 20px 20px 0;
}

.dialog-header h3 {
  margin: 0;
  color: #333;
  font-size: 18px;
}

.dialog-body {
  padding: 20px;
}

.dialog-body p {
  margin: 0;
  color: #666;
  line-height: 1.5;
}

.dialog-footer {
  padding: 20px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.3s;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background-color: #5a6268;
}

.btn-primary {
  background-color: #007bff;
  color: white;
}

.btn-primary:hover {
  background-color: #0069d9;
}

.btn-danger {
  background-color: #dc3545;
  color: white;
}

.btn-danger:hover {
  background-color: #c82333;
}

.btn-success {
  background-color: #28a745;
  color: white;
}

.btn-success:hover {
  background-color: #218838;
}

.business-name {
  color: #888;
  font-size: 12px;
  display: block;
}
</style>